import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { IColumn, IContentTable } from  '@/interfaces/IContentTable';
import { BuildPath } from  '@/helpers/Paths';
import {uuidv4} from  '@/helpers/Text'
import CellContainer from  '@/components/elements/insights/CellContainer';
import { OrderBy, Direction } from  '@/interfaces/insightsp2';
import ToolTipWrapper from '../Tooltip/ToolTipWrapper';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import OrderIcon from './OrderIcon';
import {tooltipData} from '../../../data/insights';
import { matchColumnOrder } from  '@/helpers/Insights';
import { IInsightsContext, InsightsContext } from '@/context/InsightsContext';
import  '@/css/contentTable.scss'

type Props = {
    content:IContentTable, 
    className:string, 
    setOrder:(OrderBy:OrderBy) => void,
    defaultOrder?: OrderBy,
    detailsModal?: boolean
}

const ContentTable = ({content, className, setOrder, defaultOrder, detailsModal}: Props) => {

    const [dataFilterd, setDataFiltered] = useState<any[]>([]);
    const [columnsFiltered, setColumnsFiltered] = useState<IColumn[]>([]);
    const [showMax, setShowMax] = useState<boolean>(false);

    // Context 
    const context:IInsightsContext = useContext(InsightsContext);

    // Loading
    const [initLoad, setInitLoad] = useState<boolean>(true);

    // Pagination
    const min = 5;
    const max = 100;

    useEffect(() => {

        // This is mutating instead of new copy for col check as dataset too large and would create overhead
        matchColumnOrder(content);
        
        addUrlTitle();

        loadData();

        // Set default order if exists
        if (defaultOrder){
           
            content.columns.forEach(col => {
                if (col.field === defaultOrder.column){
                    col.order = defaultOrder.direction
                }
            });
        }
        
        setColumnsFiltered(content.columns);

        setInitLoad(false);
              
    }, [content]);

    useEffect(() => {

        if (!initLoad){
            loadData();
        }

    }, [showMax]);

    // Inform context of render finished for loading
    useLayoutEffect(() => {
       context.loadTracker.dispatch({type: 'increment'})
    }, [content]);

    function addUrlTitle(){

        content.data.forEach((item, idx) => {

            if (content.data[idx].hasOwnProperty("title") && content.data[idx].hasOwnProperty("uri")){
                content.data[idx]["title"] =  {
                    label: item["title"],
                    uri: item["uri"]
                }
            }

        });
    }

    function loadData(): void {

        if (!content.data){
            return;
        }

        const data = [...content.data];

        // Remove keys in dataset that dont exist in local column list
        if (data.length){
            Object.keys(data[0]).forEach((field) => {
                if (!content.columns.filter(x => x.field === field).length){
                    data.forEach((row) => {
                        delete row[field];
                    })
                }
            });
        }
   
        if (showMax){
            setDataFiltered(data.slice(0, max));
        }
        else{
            setDataFiltered(data.slice(0, min));
        }
    }

    function headerFilter(column:string): void {

        const newOrder = [...columnsFiltered];
        let selectedItem:IColumn = null;

        for (let item of newOrder){
            if (item.field === column){
                if (item.order === null || item.order === Direction.asc){
                    item.order = Direction.dsc;
                }
                else {
                    item.order = Direction.asc;
                }

                // Save selected
                selectedItem = item;
            }
            else{
                // Reset rest
                item.order = null;
            }
        }

        if (selectedItem){

            setOrder({
                column:column,
                direction: selectedItem.order
            })

            setColumnsFiltered([...newOrder]);
        } 
    }
    
    const Header = ({col}:{col:IColumn}): JSX.Element => {

        const currentOrder = columnsFiltered.filter(x => x.field === col.field)[0];

        if (!col.display){
            return null
        }

        if (!col.sortable){
            return <span className="no-order">{col.label}</span>
        }

        return(
            <span className={currentOrder.order ? 'selected-header' : ''} onClick={() => headerFilter(col.field)}>
                <OrderIcon order={currentOrder.order} />{col.label}
            </span>
        ) 
    }
    
    const uniqueId = uuidv4();
    let count = 1;
 
    return (
        <section className="content-table-area">

            <div className="table-header-container">

                <h2 className="table-header" aria-label='Selected Content Title'>{content.title}</h2> 

                {(content.tooltip && content.title) &&

                    <ToolTipWrapper id={uniqueId} className="insights-tooltip-wrapper" >                     
                        <ReactTooltip place="top" className='thrive-tooltip' delayHide={1000}  id={uniqueId} >
                            <span>{content.tooltip}</span>
                        </ReactTooltip>
                    </ToolTipWrapper>
                }

            </div>

            {!dataFilterd.length && 
                <div className="no-results"> 
                    <div>
                        <img src={`${BuildPath()}/images/unsplash/missing.svg`} alt="Error" /> 
                        <span>No insights found for this selection</span>
                    </div>
                </div>
            }
            
            {dataFilterd.length > 0 && 

                <>
                    <div className={`content-table ${className}`}> 

                    {columnsFiltered.map((col:IColumn, idx:number) => (
                            <div className="in-header" key={idx}>
                                <Header col={col} />
                            </div>
                        )
                    )}

                    {dataFilterd.map((data:any, idx1:number) => (  
                        
                        Object.keys(data).map((val, idx2:number) => (

                            <CellContainer 
                                key={`${idx1}${idx2}`} 
                                data={data} 
                                val={val} 
                                idx={idx1} 
                                columnsFiltered={columnsFiltered} 
                                count={count++} 
                                total={dataFilterd.length * columnsFiltered.length} 
                                detailsModal={detailsModal}
                            />                 

                        ))
                    ))}

                    </div>

                    <div className="table-options">

                        {!showMax && content.data.length > 5 && 
                            <span className="load-all" onClick={() => setShowMax(true)}>Load all</span>
                        }

                        {showMax && content.data.length > 5 &&
                            <span className="load-all" onClick={() => setShowMax(false)}>Close</span>
                        }

                        {showMax && <div>&nbsp;</div>}

                        <div>&nbsp;</div>

                        {content.csvDownload && 

                            <a className="download-stats intooltip" href={content.csvDownload} aria-label="Download">

                                <img src={`${BuildPath()}/images/insightsp2/DownloadStatsBtn.svg`} alt="" />

                                <span className="tooltiptext">{tooltipData.download}</span>
                            </a>
                    
                        }

                    </div>
                </>
            }
            
        </section>
    );
};

export default ContentTable;

